<!--
  SuperAdmin: Access Control -- capability vertical list.

  Wired to:
    GET /admin/acl/roles         -> role list (via aclAdminService.listRoles)
    GET /admin/acl/capabilities  -> capability list (includes key + description)
    GET /admin/acl/roles/:id/capabilities -> per-role capability set

  Layout: vertical capability list.
    - Each row = one capability: name (key) + description + one column per role.
    - Capabilities arrive ordered by key from the API; they are displayed in
      that order. Keys follow a dot-separated prefix convention
      (campaign.*, order.*, superadmin.*) which keeps related grants adjacent
      without needing explicit grouping logic.

  Role columns: capabilities are rows; roles are columns (thead + per-row cells).

  Write operations (grant/revoke) are gated on superadmin.settings on
  the backend. The UI renders a confirm dialog before every revoke to
  guard against accidental removals.

  Checkbox testid: sa-acl-cb-<roleId>-<capKey>  (string key, not numeric id)
  Capability row testid: sa-acl-cap-row

  Membership Set is keyed by capability KEY STRING (what the API returns).
  add/remove endpoints take the numeric cap.id in the URL -- keep both.

  Read-only view is visible to any user who can reach the page (gated on
  superadmin.access at the route level).

  ASCII-only.
-->
<template>
    <div data-testid="sa-access-control">
        <h1 class="sa-page-title">Access Control</h1>

        <p
            v-if="loading"
            class="sa-state"
            data-testid="sa-acl-loading"
        >
            Loading...
        </p>

        <p
            v-else-if="errorMsg"
            class="sa-state sa-state--error"
            data-testid="sa-acl-error"
        >
            {{ errorMsg }}
        </p>

        <template v-else>
            <!-- Capability x Role matrix (vertical: cap rows, role columns) -->
            <div class="sa-matrix-wrap" data-testid="sa-acl-matrix">
                <table class="sa-matrix">
                    <thead>
                        <tr>
                            <th class="sa-matrix__cap-name-col">Capability</th>
                            <th class="sa-matrix__cap-desc-col">Description</th>
                            <th
                                v-for="role in roles"
                                :key="role.id"
                                class="sa-matrix__role-col"
                            >
                                {{ role.name }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="cap in capabilities"
                            :key="cap.id"
                            class="sa-matrix__row"
                            data-testid="sa-acl-cap-row"
                        >
                            <td class="sa-matrix__cap-key">
                                {{ cap.key }}
                            </td>
                            <td class="sa-matrix__cap-desc">
                                {{ cap.description || "" }}
                            </td>
                            <td
                                v-for="role in roles"
                                :key="role.id"
                                class="sa-matrix__cell"
                            >
                                <label
                                    class="sa-matrix__cb-label"
                                    :title="
                                        hasCapability(role.id, cap.key)
                                            ? `Revoke ${cap.key} from ${role.name}`
                                            : `Grant ${cap.key} to ${role.name}`
                                    "
                                >
                                    <input
                                        type="checkbox"
                                        class="sa-matrix__cb"
                                        :data-testid="`sa-acl-cb-${role.id}-${cap.key}`"
                                        :checked="hasCapability(role.id, cap.key)"
                                        :disabled="mutating"
                                        @change="
                                            onToggle(
                                                role,
                                                cap,
                                                $event.target.checked
                                            )
                                        "
                                    />
                                </label>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Mutation status banner -->
            <p
                v-if="mutationMsg"
                class="sa-mutation-msg"
                :class="{ 'is-error': mutationError }"
                data-testid="sa-acl-mutation-msg"
            >
                {{ mutationMsg }}
            </p>

            <!-- Confirm-revoke dialog (native confirm for simplicity) -->
        </template>
    </div>
</template>

<script>
import {
    listRoles,
    listCapabilities,
    getRoleTemplateCapabilities,
    addRoleTemplateCapability,
    removeRoleTemplateCapability,
} from "@/services/aclAdminService";
import { emitSidebarRefresh } from "@/stores/sidebarRefresh";

export default {
    name: "SuperAdminAccessControl",
    data() {
        return {
            loading: true,
            errorMsg: "",
            roles: [],
            capabilities: [],
            // Map: role_id -> Set<cap_key_string>
            roleCapMap: {},
            mutating: false,
            mutationMsg: "",
            mutationError: false,
        };
    },
    async mounted() {
        await this.loadAll();
    },
    methods: {
        // The per-role capability set is keyed by capability KEY STRINGS
        // (what GET /admin/acl/roles/:id/capabilities returns). The add/remove
        // endpoints take the numeric cap.id in the URL path -- keep both.
        hasCapability(roleId, capKey) {
            return !!(
                this.roleCapMap[roleId] &&
                this.roleCapMap[roleId].has(capKey)
            );
        },

        async loadAll() {
            this.loading = true;
            this.errorMsg = "";
            try {
                const [rolesResp, capsResp] = await Promise.all([
                    listRoles(),
                    listCapabilities(),
                ]);
                const rolesData =
                    rolesResp && rolesResp.data ? rolesResp.data : rolesResp;
                const capsData =
                    capsResp && capsResp.data ? capsResp.data : capsResp;

                this.roles = (rolesData && rolesData.roles) || [];
                this.capabilities =
                    (capsData && capsData.capabilities) || [];

                // Load each role's capability set in parallel.
                // Surface fetch errors per-role rather than silently treating
                // a failed fetch as "no capabilities" (which hides real errors).
                const capEntries = await Promise.all(
                    this.roles.map(async (role) => {
                        const r = await getRoleTemplateCapabilities(role.id);
                        const d = r && r.data ? r.data : r;
                        // API returns string keys: ["campaign.view", ...]
                        const keys = (d && d.capabilities) || [];
                        return [role.id, keys];
                    })
                );

                const map = {};
                for (const [roleId, keys] of capEntries) {
                    // keys are already strings; build a Set<string> for O(1) lookup.
                    map[roleId] = new Set(keys);
                }
                this.roleCapMap = map;
            } catch (e) {
                this.errorMsg =
                    "Could not load access control data. Please try again.";
            } finally {
                this.loading = false;
            }
        },

        async onToggle(role, cap, checked) {
            if (
                !checked &&
                !window.confirm(
                    `Revoke "${cap.key}" from role "${role.name}"?\nThis affects all users with this role.`
                )
            ) {
                // User cancelled the revoke confirm dialog. The checkbox
                // was toggled to unchecked by the browser before the confirm
                // fired; hasCapability still returns true (roleCapMap unchanged),
                // so we must force a Vue re-render to restore the visual state.
                this.roleCapMap = { ...this.roleCapMap };
                return;
            }

            this.mutating = true;
            this.mutationMsg = "";
            this.mutationError = false;

            try {
                if (checked) {
                    // add/remove endpoints take the numeric cap.id in the URL.
                    await addRoleTemplateCapability(role.id, cap.id, {
                        reason: "SA console grant",
                    });
                    if (!this.roleCapMap[role.id]) {
                        this.roleCapMap[role.id] = new Set();
                    }
                    this.roleCapMap[role.id].add(cap.key);
                } else {
                    await removeRoleTemplateCapability(role.id, cap.id, {
                        reason: "SA console revoke",
                    });
                    if (this.roleCapMap[role.id]) {
                        this.roleCapMap[role.id].delete(cap.key);
                    }
                }
                // Force Vue reactivity on the map (Set mutation is not
                // reactive by itself).
                this.roleCapMap = { ...this.roleCapMap };
                this.mutationMsg = checked
                    ? `Granted ${cap.key} to ${role.name}.`
                    : `Revoked ${cap.key} from ${role.name}.`;
                // A grant OR revoke moves the SA sidebar ACCESS CONTROL count
                // -> refresh it. Reached only after the add/remove await
                // resolves (a failed mutation throws to the catch); the
                // cancelled-revoke path returns early above and never gets
                // here. Generic delegated seam (https://trello.com/c/g9K52FsA).
                emitSidebarRefresh("access_control");
            } catch (e) {
                this.mutationError = true;
                const serverMsg =
                    e?.response?.data?.message ?? e?.data?.message;
                this.mutationMsg = serverMsg
                    ? `Could not save change: ${serverMsg}`
                    : "Could not save change. Please try again.";
                // Revert the visual checkbox state on error.
                this.roleCapMap = { ...this.roleCapMap };
            } finally {
                this.mutating = false;
            }
        },
    },
};
</script>

<style scoped>
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 16px;
    color: #1a202c;
}
.sa-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-state--error {
    color: #c53030;
}
.sa-matrix-wrap {
    overflow-x: auto;
}
.sa-matrix {
    border-collapse: collapse;
    font-size: 13px;
    width: 100%;
}
.sa-matrix th,
.sa-matrix td {
    border: 1px solid #e2e8f0;
    padding: 8px 10px;
    vertical-align: top;
}
.sa-matrix th {
    background: #f7fafc;
    font-weight: 600;
    text-align: left;
    color: #2d3748;
    white-space: nowrap;
}
.sa-matrix__cap-name-col {
    min-width: 180px;
}
.sa-matrix__cap-desc-col {
    min-width: 260px;
    font-weight: 400;
    color: #4a5568;
    font-size: 12px;
}
.sa-matrix__role-col {
    min-width: 80px;
    text-align: center;
}
.sa-matrix__row td {
    vertical-align: middle;
}
.sa-matrix__cap-key {
    font-family: monospace;
    font-size: 12px;
    color: #2d3748;
    font-weight: 500;
}
.sa-matrix__cap-desc {
    font-size: 12px;
    color: var(--tf-fg-3);
}
.sa-matrix__cell {
    text-align: center;
    padding: 4px;
}
.sa-matrix__cb-label {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}
.sa-matrix__cb {
    width: 14px;
    height: 14px;
    cursor: pointer;
}
.sa-matrix__cb:disabled {
    cursor: wait;
    opacity: 0.5;
}
.sa-mutation-msg {
    margin-top: 10px;
    font-size: 13px;
    color: #2d3748;
    padding: 8px 12px;
    background: #f0fff4;
    border: 1px solid #9ae6b4;
    border-radius: 4px;
}
.sa-mutation-msg.is-error {
    background: #fff5f5;
    border-color: #feb2b2;
    color: #c53030;
}
</style>
